import type { CensusRow, CensusView } from '../types.js'
import { describeBasis, rowFor, shortDay } from './censusCardData.js'

interface CensusTableProps {
  view: CensusView
  today: string
  /** The dates to show, in order; the view's whole range when left out. */
  dates?: readonly string[]
  /** A cell chosen for the What-if, highlighted. */
  selected?: { date: string; unitId: string } | null
  onSelect?: (date: string, unitId: string) => void
}

function cellClass(row: CensusRow | null, isToday: boolean, isSelected: boolean): string {
  const base = isSelected ? 'ring-2 ring-coral' : ''
  if (!row || row.census === null) {
    return `${base} text-ink-mid/50`
  }
  if (row.basis === 'recorded') {
    return `${base} font-bold ${row.overBeds ? 'text-coral' : 'text-ink'}${isToday ? ' bg-marine/10' : ''}`
  }
  return `${base} italic text-ink-mid`
}

/** "42" with the basis in the title; a forecast in grey italics, a recorded count bold, nothing where there is none. */
function CensusCell({ row, isToday, isSelected, onClick }: { row: CensusRow | null; isToday: boolean; isSelected: boolean; onClick?: () => void }) {
  const text = row && row.census !== null ? String(row.census) : '—'
  const title = row && row.census !== null ? describeBasis(row, row.date) : 'no count and nothing to forecast from'
  const className = `w-full rounded px-2 py-1 text-center text-[11px] ${cellClass(row, isToday, isSelected)}${onClick ? ' hover:bg-canvas' : ''}`
  return onClick ? (
    <button type="button" title={title} aria-label={`${row?.date ?? ''} ${row?.unitId ?? ''}: ${text}, ${title}`} className={className} onClick={onClick}>
      {text}
    </button>
  ) : (
    <span title={title} className={`block ${className}`}>
      {text}
    </span>
  )
}

/**
 * The census as a grid: a row per date, a column per unit, recorded counts
 * bold and forecasts in grey italics, today marked, with the PPD per unit at
 * the foot. Shared by the Census tool, the admin dialog and the simulator.
 */
export function CensusTable({ view, today, dates, selected = null, onSelect }: CensusTableProps) {
  const shown = dates ?? [...new Set(view.rows.map((row) => row.date))]
  if (view.units.length === 0) {
    return <p className="text-[11px] text-ink-mid">No units to show. List the facility’s units under Configuration › Details, with their beds and planned census.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-marine">
            <th className="py-1 pr-2">Date</th>
            {view.units.map((unit) => (
              <th key={unit.id} className="py-1 px-2 text-center">
                {unit.name}
                {unit.beds !== null && <span className="block font-normal normal-case tracking-normal text-ink-mid">{unit.beds} beds</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-marine/10">
          {shown.map((date) => (
            <tr key={date} className={date === today ? 'bg-marine/5' : ''}>
              <td className="py-0.5 pr-2 whitespace-nowrap text-ink-mid">
                {shortDay(date)}
                {date === today && <span className="ml-1 text-[9px] font-bold uppercase text-marine">today</span>}
              </td>
              {view.units.map((unit) => (
                <td key={unit.id} className="px-1 py-0.5">
                  <CensusCell row={rowFor(view, date, unit.id)} isToday={date === today} isSelected={selected?.date === date && selected.unitId === unit.id} onClick={onSelect ? () => onSelect(date, unit.id) : undefined} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="text-[10px] text-ink-mid">
            <td className="pt-1 pr-2">PPD over the range</td>
            {view.ppd.map((entry) => (
              <td key={entry.unitId} className="pt-1 px-2 text-center">
                {entry.recorded} recorded{entry.forecast > 0 && ` · ${entry.forecast} forecast`}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}
